import { X } from 'lucide-react';
import { useId, useRef, type ReactNode } from 'react';

import { useFocusTrap } from '../lib/useFocusTrap';

interface Props {
  title: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}

/** Right-hand side panel for create/edit forms; modal, focus-trapped. */
export function Drawer({ title, description, onClose, children, footer }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descId = useId();
  useFocusTrap(ref, onClose);

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div aria-hidden="true" className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className="relative flex h-full w-full max-w-md flex-col border-l border-line-strong bg-surface shadow-2xl shadow-black/50 focus:outline-none"
      >
        <header className="flex items-start justify-between gap-4 border-b border-line px-4 py-4 sm:px-5">
          <div className="min-w-0">
            <h2 id={titleId} className="text-[15px] font-semibold text-fg">
              {title}
            </h2>
            {description && (
              <p id={descId} className="mt-0.5 truncate text-xs text-fg-muted">
                {description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="grid size-9 shrink-0 place-items-center rounded-md text-fg-subtle hover:bg-surface-hover hover:text-fg sm:size-7"
          >
            <X aria-hidden="true" className="size-4" strokeWidth={2} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-5">{children}</div>
        {footer && (
          <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-4 py-3 sm:px-5">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}
